/**
 * Utility functions for CSV generation and download
 */

/**
 * Escapes a CSV field value by wrapping in quotes if it contains commas, quotes, or newlines
 */
export function escapeCSVField(value: any): string {
  if (value === null || value === undefined) {
    return "";
  }

  // Handle arrays and objects by JSON stringifying them
  let stringValue: string;
  if (Array.isArray(value)) {
    // Join array values with a separator that won't conflict with CSV
    stringValue = value.map((v) => (v === null || v === undefined ? "" : String(v))).join("; ");
  } else if (typeof value === "object" && value !== null) {
    // For objects (including Date), try to get a meaningful string representation
    if (value instanceof Date) {
      stringValue = value.toISOString();
    } else {
      stringValue = JSON.stringify(value);
    }
  } else {
    stringValue = String(value);
  }

  // Check if the field needs to be quoted (commas, quotes, newlines)
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r")
  ) {
    // Escape existing quotes by doubling them
    const escapedValue = stringValue.replace(/"/g, '""');
    return `"${escapedValue}"`;
  }

  return stringValue;
}
function flattenObject(obj: any, parentKey = "", res: Record<string, any> = {}): Record<string, any> {
  // Handle null/undefined early
  if (obj === null || obj === undefined) return res;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const propName = parentKey ? `${parentKey}.${key}` : key;
      const value = obj[key];

      if (value instanceof Date) {
        // Keep Dates as objects so escapeCSVField can call .toISOString()
        res[propName] = value;
      } else if (Array.isArray(value)) {
        // Recurse into arrays using the index as the key
        if (value.length === 0) {
          res[propName] = "";
        } else {
          flattenObject(value, propName, res);
        }
      } else if (typeof value === "object" && value !== null) {
        // Recurse into nested objects
        flattenObject(value, propName, res);
      } else {
        // Leaf value (string, number, boolean)
        res[propName] = value;
      }
    }
  }
  return res;
}
/**
 * Converts an array of objects to CSV format
 */
export function convertToCSV(
  data: any[],
  providedColumns?: string[],
  headerFunc?: (key: string) => string,
  override?: Record<string, string>,
): string {
  if (data.length === 0) return "";

  // 1. Always flatten the data first
  const flattenedData = data.map((row) => flattenObject(row));

  // 2. Determine which columns to use
  let columns: string[];

  if (providedColumns && providedColumns.length > 0) {
    // If the user provided columns like ['user.name', 'id'], use them.
    // If they provided ['user'], we need to find all flattened keys that START with 'user.'
    columns = [];
    providedColumns.forEach((pCol) => {
      const matchingKeys = new Set<string>();
      flattenedData.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (key === pCol || key.startsWith(`${pCol}.`)) {
            matchingKeys.add(key);
          }
        });
      });

      if (matchingKeys.size > 0) {
        columns.push(...Array.from(matchingKeys).sort());
      } else {
        // If no matches found, keep the original requested column (will be empty)
        columns.push(pCol);
      }
    });
  } else {
    // Auto-discovery: get every single key from every row
    columns = Array.from(new Set(flattenedData.flatMap((row) => Object.keys(row)))).sort();
  }

  const transformHeader = (key: string): string => {
    if (override && key in override) return override[key];
    return headerFunc?.(key) ?? key;
  };

  // 3. Create header row
  const headers = columns.map((col) => escapeCSVField(transformHeader(col)));
  const csvRows = [headers.join(",")];

  // 4. Create data rows
  for (const flatRow of flattenedData) {
    const csvRow = columns.map((col) => {
      // Access the value from the pre-flattened object
      return escapeCSVField(flatRow[col]);
    });
    csvRows.push(csvRow.join(","));
  }

  return csvRows.join("\n");
}

/**
 * Downloads a CSV string as a file
 */
export function downloadCSV(csvContent: string, filename: string = "export.csv"): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  // Create download link
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
}

/**
 * Generates a filename with timestamp
 */
export function generateFilename(prefix: string = "export"): string {
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 19).replace(/[:-]/g, "");
  return `${prefix}_${timestamp}.csv`;
}
